import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useLocation, useNavigate } from 'react-router-dom';
import VendorCard from '@/components/VendorCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Filter, Loader2, X } from 'lucide-react';
import axios from 'axios';
import { vendorCategories as defaultCategories, vendorSlugToCategory, vendorCategoryToSlug } from '@/lib/vendorCategories';
import { categoryNameToSeoPath, formatWeddingCategoryLabel } from '@/lib/vendorCategorySeo';
import { getSeoPreviewMode } from '@/lib/seoPreviewMode';
import { isCategoryEnabled, mergeOrderedCategoryObjects } from '@/lib/vendorCategorySource';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slugToCategory = vendorSlugToCategory;
const categoryToSlug = vendorCategoryToSlug;

const WEDDING_VENUES_SEO_SLUG = 'wedding-venues-gibraltar';

const CHURCH_CONTACTS = [
  {
    id: 'st-mary-crowned',
    name: 'Cathedral of St Mary the Crowned',
    phone: '+350 200 76688',
    email: 'smccathedral@gmail.com',
    website: 'https://catholic.gi/diocese/parishes/the-cathedral-of-mary-the-crowned/',
    summary: 'A central Roman Catholic cathedral often considered for church wedding ceremonies in Gibraltar.',
  },
  {
    id: 'holy-trinity',
    name: 'Cathedral Church of the Holy Trinity',
    phone: '+350 200 75745',
    email: '',
    website: 'https://www.holytrinitygibraltar.org/',
    summary: 'An Anglican cathedral in Gibraltar with wedding services and ceremony enquiries handled by the church office.',
  },
];

const isWeddingVenueVendor = (vendor) => {
  const categoryValues = [
    vendor?.category,
    ...(Array.isArray(vendor?.categories) ? vendor.categories : []),
    vendor?.primaryCategory,
    ...(Array.isArray(vendor?.secondaryCategories) ? vendor.secondaryCategories : []),
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  return categoryValues.some((value) => value.includes('venue') || value.includes('hotel'));
};

const detectSeoCategoryType = (categoryName) => {
  const value = String(categoryName || '').toLowerCase();
  if (value.includes('photo') || value.includes('video')) return 'photography';
  if (value.includes('florist') || value.includes('flower')) return 'florists';
  if (value.includes('jewel') || value.includes('ring') || value.includes('band')) return 'jewellery';
  if (value.includes('entertain') || value.includes('dj') || value.includes('musician') || value.includes('music')) return 'entertainment';
  if (value.includes('planner') || value.includes('planning') || value.includes('all in one') || value.includes('registry package') || value.includes('package') || value.includes('elope')) return 'planning';
  if (value.includes('stationery') || value.includes('invite')) return 'stationery';
  if (value.includes('attire') || value.includes('dress') || value.includes('suit')) return 'attire';
  if (value.includes('cake') || value.includes('cater')) return 'catering';
  if (value.includes('hair') || value.includes('beauty')) return 'beauty';
  if (value.includes('venue') || value.includes('hotel') || value.includes('accomodation') || value.includes('accommodation')) return 'venues';
  return 'general';
};

const normalizeCategoryKey = (value) => String(value || '').toLowerCase().replace(/^wedding\s+/, '').trim();

const buildSeoCategoryContent = (categoryLabel) => {
  const category = String(categoryLabel || 'Wedding Services').trim();
  const categoryLower = category.toLowerCase();
  const categoryKey = normalizeCategoryKey(category);
  const contentType = detectSeoCategoryType(category);

  const templates = {
    photography: {
      intro1: `Wedding photography and filming shape how your day is remembered. The right ${categoryLower} team captures candid moments, emotional reactions, and the details that become your wedding albums and films.`,
      intro2: `Gibraltar gives photographers and videographers dramatic coastal light, historic streets, and iconic Rock backdrops that work beautifully for intimate ceremonies and larger celebrations.`,
      why: [
        `Gibraltar wedding ${categoryLower} suppliers know how to work quickly across registry ceremonies, outdoor viewpoints, and hotel venues, so you get strong results even with tight timelines.`,
        `Local teams also understand light shifts throughout the day, helping you balance portraits, candid coverage, and storytelling across photo sets and video edits.`,
      ],
      lookFor: [
        `Review full galleries, not only highlight images, to check consistency in candid moments, colour editing, and how couples are photographed in different lighting.`,
        `Ask about coverage hours, second-shooter options, delivery times, and album or film formats so your wedding photography package matches your priorities.`,
      ],
      browse: `Browse Gibraltar-based ${categoryLower}, compare visual style and package options, then contact suppliers to check availability for your wedding date.`,
    },
    florists: {
      intro1: `Flowers set the tone for your ceremony and celebration. Great ${categoryLower} suppliers bring your bouquets, floral arrangements, and table styling together in a way that feels cohesive.`,
      intro2: `In Gibraltar, florists regularly design for registry weddings, church ceremonies, and coastal venues, adapting floral choices to weather, transport timing, and seasonality.`,
      why: [
        `Gibraltar wedding florists understand which blooms travel well, hold shape in warmer weather, and photograph beautifully in outdoor ceremonies.`,
        `Working with local specialists helps coordinate bouquets, buttonholes, ceremony florals, and reception designs without last-minute logistical issues.`,
      ],
      lookFor: [
        `Check the florist's style range, colour palettes, and previous bouquet or centerpiece work to confirm they can deliver your preferred look.`,
        `Ask about seasonal flower availability, setup and breakdown timing, and whether floral arrangements can be customised for your ceremony format.`,
      ],
      browse: `Compare Gibraltar wedding ${categoryLower}, shortlist designs that fit your style, and contact florists early to secure date availability.`,
    },
    jewellery: {
      intro1: `${category} is central to wedding planning because rings and bespoke jewellery become lasting symbols of your day. Quality materials and craftsmanship matter.`,
      intro2: `Gibraltar jewellers offer practical access for couples planning from abroad, with options for engagement rings, wedding bands, and custom design work.`,
      why: [
        `Gibraltar-based jewellers can guide couples through metal choices, stone quality, and ring sizing with clear timelines for weddings and elopements.`,
        `Local workshops and specialist stores often provide bespoke jewellery adjustments that help couples finalise pieces before ceremony day.`,
      ],
      lookFor: [
        `Look for transparent material details, diamond or gemstone certification, and evidence of reliable aftercare for resizing or polishing.`,
        `Compare bespoke design capability, production lead times, and wedding band pairing advice before you commit.`,
      ],
      browse: `Browse jewellery suppliers for weddings in Gibraltar, compare design styles, and contact specialists for availability and bespoke consultations.`,
    },
    entertainment: {
      intro1: `${category} drives atmosphere. From live music to DJs and performers, the right choice shapes the energy of your ceremony, drinks reception, and evening celebration.`,
      intro2: `Gibraltar entertainment suppliers regularly perform at destination weddings and know how to adapt setlists, sound setup, and timings to different venues.`,
      why: [
        `Gibraltar-based entertainment providers know venue sound constraints, setup windows, and how to keep transitions smooth between ceremony and party moments.`,
        `Local DJs, musicians, and performers can tailor playlists and live sets to mixed guest groups, helping your wedding feel personal and lively.`,
      ],
      lookFor: [
        `Check performance style, sample mixes or videos, equipment quality, and whether the act suits your crowd size and ceremony format.`,
        `Ask about setup logistics, break times, song requests, and contingency plans so entertainment runs cleanly from start to finish.`,
      ],
      browse: `Compare entertainment options for weddings in Gibraltar, review performance style, and message providers to align on timing and atmosphere.`,
    },
    planning: {
      intro1: `${category} services are ideal for couples who want clear coordination, bundled options, and fewer moving parts while planning a wedding in Gibraltar.`,
      intro2: `Gibraltar planners and package providers can combine legal preparation, supplier management, and timeline control, which is especially valuable for destination weddings.`,
      why: [
        `Gibraltar wedding planners understand local legal steps, supplier schedules, and venue logistics, reducing admin pressure before your ceremony.`,
        `All-in-one package providers can combine planning, styling, photography, and on-the-day coordination so couples manage fewer separate contracts.`,
      ],
      lookFor: [
        `Review exactly what is included: legal support, planning scope, communication frequency, and which suppliers are bundled into the package.`,
        `Ask for planning timelines, contingency handling, and examples of weddings similar to your guest size and ceremony goals.`,
      ],
      browse: `Browse Gibraltar-based ${categoryLower}, compare package structures, and contact providers to discuss availability and included services.`,
    },
    stationery: {
      intro1: `${category} helps shape first impressions of your wedding through invitations, signage, menus, and day-of printed details.`,
      intro2: `Suppliers in Gibraltar can tailor print and design work to destination timelines, ensuring stationery arrives in time for travel and guest coordination.`,
      why: [
        `Local stationery specialists can align your invitation suite with venue style, ceremony format, and multilingual guest needs.`,
        `Gibraltar-based production can reduce delays and simplify changes to details such as timings, addresses, or seating information.`,
      ],
      lookFor: [
        `Check paper quality, typography options, and whether the supplier can keep design consistency across invitations, menus, and signage.`,
        `Ask about production lead times, proofing process, and delivery coordination for destination weddings.`,
      ],
      browse: `Browse wedding ${categoryLower} in Gibraltar, compare design style and print quality, then contact suppliers for timelines and pricing.`,
    },
    attire: {
      intro1: `${category} decisions influence comfort, confidence, and style throughout your ceremony and celebration day.`,
      intro2: `Gibraltar boutiques and stylists support couples with wedding attire options suited to local weather, venue type, and destination travel schedules.`,
      why: [
        `Gibraltar wedding attire providers understand practical fitting timelines for overseas couples who may arrive close to the ceremony date.`,
        `Local expertise helps coordinate dresses, suits, accessories, and alteration timing so final looks are ready without stress.`,
      ],
      lookFor: [
        `Review style range, fitting process, and alteration capability to make sure the supplier can deliver your preferred silhouette and finish.`,
        `Ask about lead times, trial appointments, and what support is available for last-minute adjustments.`,
      ],
      browse: `Compare ${categoryLower} suppliers for weddings in Gibraltar, review style collections, and contact vendors to schedule fittings.`,
    },
    catering: {
      intro1: `${category} planning affects guest experience more than almost any other element, from ceremony refreshments to full reception dining.`,
      intro2: `Gibraltar caterers and cake specialists can adapt menus and service format to intimate ceremonies, destination weddings, and venue-specific requirements.`,
      why: [
        `Gibraltar wedding caterers know local venue restrictions, setup logistics, and menu pacing needed for smooth service.`,
        `Local suppliers can offer flexible options for dietary requests, climate-aware presentation, and efficient on-site coordination.`,
      ],
      lookFor: [
        `Compare tasting options, menu flexibility, and service style so food delivery matches your guest profile and celebration format.`,
        `Ask about staffing, timeline management, and contingency plans for weather or delayed schedules.`,
      ],
      browse: `Browse Gibraltar wedding ${categoryLower}, compare menu and service options, and contact suppliers to check available dates.`,
    },
    beauty: {
      intro1: `${category} services help couples and bridal parties feel prepared, polished, and camera-ready throughout the wedding day.`,
      intro2: `Gibraltar hair and beauty specialists regularly support destination weddings, planning looks that hold in local weather and suit ceremony timing.`,
      why: [
        `Gibraltar-based artists know how to build makeup and hairstyle plans for warm conditions, travel schedules, and venue changes.`,
        `Local teams can manage trial sessions and wedding-day timelines for couples, bridesmaids, and family groups.`,
      ],
      lookFor: [
        `Review portfolio examples for skin tone match, hairstyle durability, and whether the artist's style aligns with your wedding vision.`,
        `Ask about trial availability, products used, and team capacity for larger wedding parties.`,
      ],
      browse: `Compare beauty suppliers for weddings in Gibraltar, shortlist artists by style, and contact teams to reserve your date.`,
    },
    venues: {
      intro1: `${category} choices shape the entire flow of your wedding, from ceremony logistics to guest comfort and visual style.`,
      intro2: `In Gibraltar, venue options include registry settings, private spaces, and hotels that work well for destination couples and local celebrations.`,
      why: [
        `Gibraltar venues are experienced in hosting legal ceremonies and destination events, with practical support around timing and supplier access.`,
        `Choosing a local venue partner helps with layout planning, guest movement, and day-of coordination.`,
      ],
      lookFor: [
        `Check capacity, ceremony permissions, weather fallback options, and supplier flexibility before booking.`,
        `Review package inclusions, schedule constraints, and whether the venue aligns with your wedding style and budget.`,
      ],
      browse: `Browse wedding venue options in Gibraltar, compare facilities and ceremony formats, and contact teams for availability.`,
    },
    general: {
      intro1: `${category} is an important part of shaping your wedding experience, influencing how smoothly your day runs and how your celebration feels.`,
      intro2: `Gibraltar offers strong options for this service area, with suppliers experienced in legal weddings, destination timelines, and venue coordination.`,
      why: [
        `Gibraltar-based ${categoryLower} providers are familiar with local wedding formats and practical delivery timelines.`,
        `Local suppliers can support couples travelling from abroad with clear communication and date-focused planning.`,
      ],
      lookFor: [
        `Review specialist experience, quality of past work, and whether the supplier's approach fits your ceremony goals.`,
        `Compare package clarity, responsiveness, and real availability before confirming bookings.`,
      ],
      browse: `Browse ${categoryLower} suppliers in Gibraltar, compare profiles, and reach out directly to discuss your wedding requirements.`,
    },
  };

  const faqByType = {
    photography: [
      {
        q: `How early should we book ${categoryLower} for a Gibraltar wedding?`,
        a: 'Booking early gives you better date availability, especially during peak seasons and sunset ceremony periods.',
      },
      {
        q: 'Do photographers in Gibraltar cover both candid and posed shots?',
        a: 'Most teams offer a mix of candid storytelling and guided portraits, but it is best to confirm coverage style before booking.',
      },
      {
        q: 'Can we request albums and highlight films together?',
        a: 'Yes, many suppliers provide bundled photography and videography packages that include albums, edits, and highlights.',
      },
    ],
    florists: [
      {
        q: `Can Gibraltar ${categoryLower} customise bouquet styles?`,
        a: 'Yes, florists typically tailor bouquets and arrangements to your palette, season, and venue setting.',
      },
      {
        q: 'How far in advance should floral design be confirmed?',
        a: 'It is best to confirm floral direction early so suppliers can secure seasonal flowers and finalise setup logistics.',
      },
      {
        q: 'Do florists handle setup at ceremony and reception venues?',
        a: 'Many do, including delivery, installation, and collection depending on your package.',
      },
    ],
    jewellery: [
      {
        q: `Can ${categoryLower} in Gibraltar create bespoke rings?`,
        a: 'Yes, many jewellers offer bespoke ring design, stone selection, and custom wedding band matching.',
      },
      {
        q: 'Should we ask for certification and material details?',
        a: 'Absolutely. Certification, metal quality, and aftercare terms should always be clear before purchase.',
      },
      {
        q: 'Can rings be resized before the wedding date?',
        a: 'Most jewellers provide resizing services, but timelines depend on design complexity and availability.',
      },
    ],
    entertainment: [
      {
        q: `What entertainment options are popular for weddings in Gibraltar?`,
        a: 'Live music, DJs, and mixed-format performers are all common depending on venue style and guest profile.',
      },
      {
        q: 'Can entertainment be tailored to our schedule?',
        a: 'Yes, most suppliers can adapt ceremony, reception, and evening sets to your preferred timeline.',
      },
      {
        q: 'Do entertainment providers bring their own equipment?',
        a: 'Many do, but it is important to confirm sound setup, power needs, and backup planning in advance.',
      },
    ],
    planning: [
      {
        q: `Do Gibraltar ${categoryLower} include legal support in packages?`,
        a: 'Many planners and all-in-one providers include legal coordination and document guidance in their service scope.',
      },
      {
        q: 'Can planners manage suppliers as a single point of contact?',
        a: 'Yes, wedding planners typically coordinate vendors, schedules, and day-of logistics to reduce admin load for couples.',
      },
      {
        q: 'Are all-in-one packages suitable for destination weddings?',
        a: 'Yes, bundled services are especially useful for destination weddings where time and coordination are critical.',
      },
    ],
    stationery: [
      {
        q: `Can ${categoryLower} suppliers design matching wedding suites?`,
        a: 'Yes, many suppliers can create coordinated invitations, signage, menus, and table stationery.',
      },
      {
        q: 'How long does wedding stationery production usually take?',
        a: 'Timelines vary by complexity, but proofing and print should be planned early to avoid delivery pressure.',
      },
      {
        q: 'Can wording and layout be customised for destination guests?',
        a: 'Yes, suppliers can adapt wording and formats to include travel, timing, and multilingual details.',
      },
    ],
    attire: [
      {
        q: `How early should we book ${categoryLower} fittings in Gibraltar?`,
        a: 'Early bookings help secure fitting windows and allow enough time for alterations before ceremony day.',
      },
      {
        q: 'Can outfits be adjusted close to the wedding date?',
        a: 'Many providers can support final adjustments, depending on tailoring schedules and garment complexity.',
      },
      {
        q: 'Do suppliers support full bridal party styling?',
        a: 'Yes, many attire specialists can coordinate styling across couples, bridal parties, and accessories.',
      },
    ],
    catering: [
      {
        q: `Can Gibraltar ${categoryLower} adapt menus for dietary needs?`,
        a: 'Most caterers can customise menus for dietary requirements when informed during planning.',
      },
      {
        q: 'Should we request tastings before confirming a caterer?',
        a: 'Yes, tastings help confirm quality, presentation, and menu fit for your wedding style.',
      },
      {
        q: 'Can service format be tailored to intimate and large weddings?',
        a: 'Yes, suppliers can usually adapt service style to ceremony size, timing, and venue setup.',
      },
    ],
    beauty: [
      {
        q: `Do Gibraltar ${categoryLower} suppliers offer trials before the wedding day?`,
        a: 'Most hair and beauty teams offer trial sessions so looks can be finalised before ceremony day.',
      },
      {
        q: 'Can artists support full bridal party preparation?',
        a: 'Yes, many providers can schedule multi-person preparation depending on team size and timeline.',
      },
      {
        q: 'How do we choose a style that lasts in local weather?',
        a: 'Review portfolio work in similar conditions and discuss products and setting techniques in advance.',
      },
    ],
    venues: [
      {
        q: `When should we reserve ${categoryLower} in Gibraltar?`,
        a: 'Early reservations are recommended, especially for popular months and weekend ceremony dates.',
      },
      {
        q: 'Can venues support both legal ceremony and reception flow?',
        a: 'Many venues can host both, but service scope and legal permissions should be confirmed upfront.',
      },
      {
        q: 'Do venues help coordinate supplier access and timings?',
        a: 'Most experienced venues provide operational guidance for setup, ceremony transitions, and supplier logistics.',
      },
    ],
    general: [
      {
        q: `How early should we book ${categoryLower} for a Gibraltar wedding?`,
        a: 'Booking early generally improves date availability and gives more time for supplier coordination.',
      },
      {
        q: `What details should we confirm before choosing ${categoryLower}?`,
        a: 'Check service scope, package transparency, and communication speed before finalising your decision.',
      },
      {
        q: 'Can we compare multiple suppliers before enquiring?',
        a: 'Yes, this page is designed to help you compare options and contact providers that best match your wedding plans.',
      },
    ],
  };

  const categoryFaqOverrides = {
    'all in one registry packages': [
      { q: 'What is usually included in Gibraltar all-in-one registry packages?', a: 'Most packages combine legal coordination, ceremony booking support, and selected supplier services, but inclusions vary by provider tier.' },
      { q: 'Are all-in-one packages cheaper than booking each service separately?', a: 'They can be more cost-efficient for many couples, especially when bundled services reduce admin time and duplicated setup costs.' },
      { q: 'What should couples compare before choosing a registry package?', a: 'Compare legal support scope, supplier quality, timeline ownership, and whether final pricing is fixed or likely to add extras.' },
    ],
    jewellers: [
      { q: 'How do couples compare wedding jewellers in Gibraltar properly?', a: 'Focus on material quality, hallmark details, craftsmanship consistency, and aftercare terms, not design appearance alone.' },
      { q: 'Can Gibraltar jewellers support custom wedding band matching?', a: 'Yes, many jewellers offer bespoke matching, profile adjustments, and stone or metal customisation for coordinated sets.' },
      { q: 'When should ring orders be placed before a Gibraltar wedding?', a: 'Ordering early is safer, especially when resizing or bespoke production is required before fixed ceremony dates.' },
    ],
    planners: [
      { q: 'What do Gibraltar wedding planners usually manage end-to-end?', a: 'Most planners coordinate supplier timelines, planning milestones, ceremony logistics, and on-the-day execution oversight.' },
      { q: 'How can couples evaluate planner quality before booking?', a: 'Review timeline structure, communication standards, contingency planning, and examples of similar weddings delivered successfully.' },
      { q: 'Are planners useful even for smaller Gibraltar weddings?', a: 'Yes, even smaller weddings benefit from timeline control and supplier coordination that reduces stress and avoidable errors.' },
    ],
    'ceremony & reception venues': [
      { q: 'What venue factors matter most for weddings in Gibraltar?', a: 'Capacity, ceremony permissions, weather fallback options, supplier access rules, and transition flow between ceremony and reception.' },
      { q: 'Do Gibraltar venues typically support legal ceremony timing?', a: 'Many do, but couples should confirm legal schedule compatibility and operational windows before paying deposits.' },
      { q: 'How early should Gibraltar wedding venues be reserved?', a: 'Popular dates and seasonal weekends book quickly, so early reservation usually gives stronger choice and pricing control.' },
    ],
    'invites/stationery': [
      { q: 'Why is stationery important for destination weddings in Gibraltar?', a: 'Clear stationery reduces guest confusion by aligning travel timing, ceremony details, and day-of logistics in one format.' },
      { q: 'What should couples ask before booking stationery suppliers?', a: 'Ask about proofing rounds, print lead times, wording flexibility, and consistency across invitations, signage, and menus.' },
      { q: 'Can stationery be adjusted if timeline details change later?', a: 'Many suppliers can update files, but changes close to print deadlines may affect cost and delivery windows.' },
    ],
    'photographers & videographers': [
      { q: 'How should couples compare photographers and videographers in Gibraltar?', a: 'Compare complete wedding stories, editing consistency, coverage structure, and delivery standards, not highlight clips only.' },
      { q: 'What coverage details should be confirmed before booking?', a: 'Confirm hours, second shooter options, ceremony-to-evening coverage scope, and final output formats.' },
      { q: 'Do Gibraltar teams handle both photo and video in one package?', a: 'Many providers offer combined packages, but couples should confirm team size and whether both outputs receive equal quality focus.' },
    ],
    attire: [
      { q: 'When should wedding attire fittings be scheduled for Gibraltar?', a: 'Schedule fittings early enough for alterations, especially when travel dates limit final fitting flexibility.' },
      { q: 'What is a common attire planning mistake for destination weddings?', a: 'Leaving final fit adjustments too late is common and can create avoidable pressure in the final ceremony week.' },
      { q: 'Can attire suppliers coordinate full couple styling?', a: 'Many can support dresses, suits, accessories, and alteration sequencing for a cohesive final look.' },
    ],
    'hair & beauty': [
      { q: 'Why are hair and beauty trials important before Gibraltar weddings?', a: 'Trials validate look durability, timing feasibility, and product suitability for weather and photography conditions.' },
      { q: 'How can couples assess beauty provider reliability?', a: 'Review portfolio quality across skin tones and styles, then confirm timeline discipline and multi-person readiness.' },
      { q: 'Can artists prepare larger bridal parties in Gibraltar?', a: 'Yes, many teams scale for groups, but capacity and start times should be confirmed early.' },
    ],
    florists: [
      { q: 'What affects wedding floral pricing in Gibraltar most?', a: 'Flower seasonality, installation complexity, transport, and ceremony-to-reception coverage are primary cost drivers.' },
      { q: 'How do couples choose the right florist style match?', a: 'Compare bouquet and installation portfolios, colour control, and consistency across ceremony and reception work.' },
      { q: 'Do florists usually manage setup and collection logistics?', a: 'Many do, but couples should confirm exact setup windows, breakdown scope, and venue access timing.' },
    ],
    'cake makers': [
      { q: 'What should couples prioritise when choosing cake makers in Gibraltar?', a: 'Taste quality, structural reliability, delivery timing, and design execution under venue conditions should come first.' },
      { q: 'Can cake makers support dietary adaptations?', a: 'Many suppliers can provide adapted options, but requirements should be confirmed early during tasting and quote stages.' },
      { q: 'How early should wedding cake design be finalised?', a: 'Final design is best locked in once guest count and venue service timing are confirmed to avoid late revisions.' },
    ],
    caterers: [
      { q: 'How do couples compare caterers for Gibraltar weddings?', a: 'Compare tasting quality, service pacing, staffing ratio, and timeline coordination rather than menu names alone.' },
      { q: 'What catering detail is often overlooked during planning?', a: 'Operational timing is often overlooked; food quality can drop when service flow is not aligned with event scheduling.' },
      { q: 'Can Gibraltar caterers handle mixed dietary guest groups?', a: 'Most caterers can adapt menus for dietary requirements when needs are discussed clearly in advance.' },
    ],
    'decor & styling': [
      { q: 'What makes wedding decor planning effective in Gibraltar?', a: 'Effective styling links visual concept with venue constraints, setup timing, and ceremony-to-reception continuity.' },
      { q: 'How should couples evaluate decor suppliers?', a: 'Assess layout planning skill, installation quality, and ability to deliver a cohesive look across multiple spaces.' },
      { q: 'Do styling teams coordinate with florists and venues?', a: 'Strong teams usually do, and that cross-supplier coordination is often critical for smooth setup execution.' },
    ],
    'djs & musicians': [
      { q: 'How do couples choose between DJs and live musicians in Gibraltar?', a: 'Choose based on energy goals, guest profile, and whether you need dynamic transitions or performance-led sets.' },
      { q: 'What should be confirmed before booking music suppliers?', a: 'Confirm set structure, sound equipment scope, key song moments, and setup/teardown timing with venue rules.' },
      { q: 'Can DJs and musicians coordinate ceremony and evening flow?', a: 'Yes, many can cover both, but timeline ownership and transitions should be agreed in writing.' },
    ],
    entertainment: [
      { q: 'What entertainment formats work well for Gibraltar weddings?', a: 'Live acts, interactive performers, and curated reception moments work well when each is matched to timeline stages.' },
      { q: 'How can couples avoid entertainment overbooking?', a: 'Plan by event moments first, then book only acts that serve a clear purpose in guest flow and atmosphere.' },
      { q: 'Do entertainment providers adapt to venue constraints?', a: 'Experienced providers usually adapt, but power, space, and timing limits should be confirmed before deposits.' },
    ],
    'hen & stag experiences': [
      { q: 'What should couples prioritise for Gibraltar hen and stag planning?', a: 'Prioritise group logistics, activity timing, and transport coordination to keep plans smooth and low-stress.' },
      { q: 'How far ahead should hen and stag experiences be booked?', a: 'Early booking improves availability for preferred activities and helps align with broader wedding-week schedules.' },
      { q: 'Can providers support mixed group preferences?', a: 'Many can tailor programmes, but clear group preferences should be shared early to avoid late restructuring.' },
    ],
    accomodation: [
      { q: 'How should couples shortlist wedding accommodation in Gibraltar?', a: 'Compare location practicality, room-block terms, transport access, and flexibility for staggered guest arrivals.' },
      { q: 'What accommodation issue causes avoidable wedding stress?', a: 'Unclear check-in/check-out coordination is common and can disrupt ceremony-day timelines for guests.' },
      { q: 'Do accommodation providers assist with group coordination?', a: 'Some do offer support, but couples should confirm group booking workflows and communication channels.' },
    ],
    travel: [
      { q: 'What is the best approach to wedding travel planning for Gibraltar?', a: 'Align flights, transfers, and ceremony logistics together instead of booking transport in isolation.' },
      { q: 'How can travel planning reduce destination wedding risk?', a: 'Buffer timings, route contingencies, and clear guest travel guidance reduce delay-related pressure.' },
      { q: 'Should couples use dedicated travel specialists for weddings?', a: 'Specialists can help when guest logistics are complex or when multiple travel windows must align with ceremony schedules.' },
    ],
    'things to do': [
      { q: 'Why include things-to-do planning for Gibraltar wedding guests?', a: 'It improves guest experience by giving structure to free time around ceremony and reception commitments.' },
      { q: 'What activities are most useful in wedding itineraries?', a: 'Activities with predictable timing and simple logistics usually work best for mixed-age and mixed-interest groups.' },
      { q: 'How can couples avoid overloading guest schedules?', a: 'Keep optional experiences balanced and leave recovery time between social events and wedding milestones.' },
    ],
    'fitness & wellness': [
      { q: 'What fitness and wellness support is most practical before weddings?', a: 'Sustainable routines, recovery planning, and realistic scheduling usually deliver stronger outcomes than short-term intensity.' },
      { q: 'How early should wellness support begin before a wedding?', a: 'Starting early gives couples time to build consistent habits without adding pressure close to ceremony dates.' },
      { q: 'Can wellness providers tailor plans for destination timelines?', a: 'Many can adapt around travel and planning constraints when timing and goals are shared clearly in advance.' },
    ],
    extras: [
      { q: 'What counts as wedding extras in Gibraltar planning?', a: 'Extras include optional enhancements that improve comfort, atmosphere, or guest experience beyond core supplier services.' },
      { q: 'How should couples prioritise extras without overspending?', a: 'Prioritise extras with clear guest impact and timeline value instead of adding features with no practical role.' },
      { q: 'When is the best stage to finalise wedding extras?', a: 'Finalise extras after core budget items are secured so optional upgrades do not disrupt essential planning.' },
    ],
    'legal services & translators': [
      { q: 'Why are legal services and translators important for Gibraltar weddings?', a: 'They help couples interpret requirements correctly, reduce submission errors, and manage document clarity confidently.' },
      { q: 'What should couples verify with legal support providers first?', a: 'Verify document scope, translation standards, processing timelines, and responsibility for final checks before submission.' },
      { q: 'Can translators support both paperwork and in-person appointments?', a: 'Many can support both contexts, but service scope and availability windows should be confirmed early.' },
    ],
    'buying a home': [
      { q: 'How does buying a home relate to Gibraltar wedding planning?', a: 'For relocating couples, property planning often follows wedding decisions, so timeline alignment improves long-term planning.' },
      { q: 'What should couples compare when exploring Gibraltar property support?', a: 'Compare market guidance quality, timeline realism, and process clarity for residency and practical relocation steps.' },
      { q: 'When should couples start home-buying discussions after wedding planning begins?', a: 'Many start early research during wedding planning, then move into active steps once ceremony and relocation timelines are clearer.' },
    ],
  };

  const selectedTemplate = templates[contentType] || templates.general;
  const categoryIntroOverrides = {
    'all in one registry packages': {
      intro1: 'All in one registry packages in Gibraltar are built for couples who want legal preparation, ceremony coordination, and core wedding services under one clear structure.',
      intro2: 'Most couples comparing package providers focus on what is included at each tier, how timelines are managed, and whether supplier quality stays consistent across the full package.',
    },
    jewellers: {
      intro1: 'Wedding jewellers in Gibraltar are a key decision point for couples prioritising ring quality, long-term durability, and confident craftsmanship before the ceremony date.',
      intro2: 'A common mistake is comparing design only without checking hallmark details, resize timelines, and aftercare terms that affect how practical rings are after purchase.',
    },
    planners: {
      intro1: 'Wedding planners in Gibraltar help couples structure legal timings, supplier scheduling, and day-of logistics so decisions are made in the right order.',
      intro2: 'From real weddings in Gibraltar, couples usually value planner responsiveness and execution clarity more than long service lists that look strong on paper but lack delivery detail.',
    },
    'ceremony & reception venues': {
      intro1: 'Ceremony and reception venues in Gibraltar influence both atmosphere and operations, from guest flow and ceremony permissions to weather planning and supplier access.',
      intro2: 'Most couples shortlist venues by visual style first, then narrow based on practical factors like setup windows, reception transitions, and fallback options for changing conditions.',
    },
    'invites/stationery': {
      intro1: 'Wedding invites and stationery in Gibraltar shape guest communication quality, helping timelines, locations, RSVP details, and event flow stay clear from the start.',
      intro2: 'A common mistake is finalising design before confirming logistics; stronger stationery planning aligns wording and print outputs with travel details, ceremony timing, and reception changes.',
    },
    'photographers & videographers': {
      intro1: 'Photographers and videographers in Gibraltar document the full wedding story, from legal ceremony moments to evening energy and guest reactions.',
      intro2: 'From real weddings in Gibraltar, couples get stronger results when they compare complete galleries, delivery standards, and filming approach instead of choosing from social highlights only.',
    },
    attire: {
      intro1: 'Wedding attire in Gibraltar combines style decisions with fitting logistics, alteration timing, and comfort planning for destination ceremonies.',
      intro2: 'Most couples avoid stress by confirming final fit windows early, especially when travel schedules are tight and ceremony dates leave little room for late adjustments.',
    },
    'hair & beauty': {
      intro1: 'Hair and beauty suppliers in Gibraltar help couples build ceremony looks that remain polished across photos, weather exposure, and full-day timelines.',
      intro2: 'A common mistake is booking without a realistic trial plan; stronger preparation includes testing products, finish style, and schedule fit for bridal party readiness.',
    },
    florists: {
      intro1: 'Wedding florists in Gibraltar influence ceremony styling, bouquet quality, and visual consistency from legal vows to reception tablescapes.',
      intro2: 'From real weddings in Gibraltar, floral plans perform best when couples align design direction with seasonal availability, setup timings, and transport practicality.',
    },
    'cake makers': {
      intro1: 'Cake makers in Gibraltar balance design, flavour, and delivery timing so wedding cakes arrive in strong condition and match the event style.',
      intro2: 'Most couples compare tasting quality, structural reliability, and venue delivery coordination before confirming decorative details and final cake size.',
    },
    caterers: {
      intro1: 'Wedding caterers in Gibraltar shape guest experience through menu quality, service pacing, and food logistics across ceremony and reception phases.',
      intro2: 'A common mistake is choosing menu style without assessing staffing levels and timing control, which often matters more for guest experience than dish selection alone.',
    },
    'decor & styling': {
      intro1: 'Wedding decor and styling suppliers in Gibraltar translate concept direction into ceremony and reception visuals that feel cohesive rather than pieced together.',
      intro2: 'From real weddings in Gibraltar, the strongest styling outcomes come from early layout planning, clear setup sequencing, and practical alignment with venue rules.',
    },
    'djs & musicians': {
      intro1: 'DJs and musicians in Gibraltar shape event momentum by managing ceremony transitions, reception atmosphere, and evening energy in real time.',
      intro2: 'Most couples get better outcomes by confirming set structure, sound coverage, and key-song timing early instead of relying on a last-minute playlist discussion.',
    },
    entertainment: {
      intro1: 'Wedding entertainment in Gibraltar adds layered guest experience through live acts, interactive formats, and memorable transition moments throughout the day.',
      intro2: 'A common mistake is booking entertainment without flow planning; stronger results come from matching each act to a specific point in the wedding timeline.',
    },
    'hen & stag experiences': {
      intro1: 'Hen and stag experiences in Gibraltar are most effective when planned with logistics-first detail around group timing, transport, and activity sequencing.',
      intro2: 'Most couples and groups prefer providers who can coordinate schedules cleanly, so pre-wedding celebrations stay fun without creating planning pressure before ceremony day.',
    },
    accomodation: {
      intro1: 'Wedding accommodation in Gibraltar affects guest convenience, timing control, and overall wedding flow, especially for destination groups arriving on different schedules.',
      intro2: 'From real weddings in Gibraltar, couples usually avoid last-minute issues by securing room-block clarity, flexible check-in terms, and transport-linked location choices.',
    },
    travel: {
      intro1: 'Wedding travel planning in Gibraltar supports smoother arrivals, reduced delay risk, and clearer ceremony-day coordination for couples and guests.',
      intro2: 'A common mistake is confirming flights before final ceremony logistics; stronger travel plans align transport, accommodation, and legal appointment timing first.',
    },
    'things to do': {
      intro1: 'Things to do in Gibraltar helps couples create a fuller guest experience beyond the ceremony, especially for destination weddings with multi-day stays.',
      intro2: 'Most couples prioritise activity options that fit travel windows and guest profiles, turning wedding visits into a better-balanced celebration weekend.',
    },
    'fitness & wellness': {
      intro1: 'Fitness and wellness support in Gibraltar helps couples prepare for wedding day confidence through realistic routines, recovery planning, and wellbeing-focused scheduling.',
      intro2: 'From real weddings in Gibraltar, sustainable preparation plans outperform intensive short-term routines, especially when couples are balancing travel and supplier decisions.',
    },
    extras: {
      intro1: 'Wedding extras in Gibraltar elevate overall guest experience through practical enhancements that improve comfort, flow, and memorable event details.',
      intro2: 'A common mistake is adding extras late without budget prioritisation; stronger planning links each extra to a clear guest or timeline benefit.',
    },
    'legal services & translators': {
      intro1: 'Legal services and translators in Gibraltar are essential for couples who need document clarity, process confidence, and precise communication across requirements.',
      intro2: 'Most couples benefit from early legal language support to prevent avoidable delays tied to document interpretation, translation timing, or submission formatting.',
    },
    'buying a home': {
      intro1: 'Buying a home services in Gibraltar supports couples who are linking wedding plans with medium- or long-term relocation and property decisions.',
      intro2: 'From real post-wedding transitions in Gibraltar, couples make stronger decisions when property planning considers legal timelines, residency goals, and lifestyle priorities together.',
    },
  };

  const categoryAeoAngles = {
    'all in one registry packages': 'Ideal for destination couples who want one provider handling legal prep, booking flow, and day-of coordination from start to finish.',
    'jewellers': 'Focus on hallmarking, setting durability, and resizing timelines so rings are ceremony-ready even with short travel windows.',
    'planners': 'Best for couples who prefer one strategic lead to coordinate legal steps, supplier timings, and guest-day logistics.',
    'ceremony & reception venues': 'Prioritise ceremony permissions, weather back-up plans, and smooth transitions from legal vows to reception celebration.',
    'invites/stationery': 'Strong stationery partners keep guest communication clear with timelines, directions, RSVP wording, and day-of signage consistency.',
    'photographers & videographers': 'Compare full wedding stories, not highlight reels only, so you can judge consistency across prep, ceremony, and evening coverage.',
    'attire': 'Plan fittings around travel dates and ask how final alterations are managed close to ceremony day.',
    'hair & beauty': 'Ask about trial bookings and humidity-proof products so your look stays polished from ceremony through evening photos.',
    'florists': 'Discuss seasonal sourcing and setup schedules to ensure bouquets and installations stay fresh for the full event timeline.',
    'cake makers': 'Confirm delivery windows, venue setup rules, and dietary adaptations before finalising your design and flavour selection.',
    'caterers': 'Evaluate tasting quality, staffing ratios, and service pacing to keep guest experience smooth throughout the reception.',
    'decor & styling': 'Choose stylists who can align ceremony styling, reception tablescapes, and lighting details into one cohesive visual direction.',
    'djs & musicians': 'Clarify set flow, key song moments, and sound constraints so music transitions feel seamless all day.',
    'entertainment': 'Use entertainment to shape energy by moment—welcoming arrivals, elevating cocktail hour, and driving evening celebration.',
    'hen & stag experiences': 'Look for planners that manage group logistics, transport, and timing so your pre-wedding events stay effortless and fun.',
    'accomodation': 'Prioritise booking flexibility and group coordination support so guest arrivals, check-ins, and wedding-day transport stay stress-free.',
    'travel': 'Travel specialists can align flights, transfers, and ceremony timelines to reduce last-minute pressure for couples and guests.',
    'things to do': 'Curate guest experiences around ceremony dates to make your Gibraltar wedding trip feel memorable beyond the wedding day itself.',
    'fitness & wellness': 'Choose providers with realistic pre-wedding plans that support confidence and wellbeing without adding planning stress.',
    'extras': 'Extras add personality—focus on practical enhancements that elevate guest comfort and strengthen your celebration atmosphere.',
    'legal services & translators': 'Legal support is crucial for document accuracy, appointment sequencing, and confidence when handling cross-border requirements.',
    'buying a home': 'If Gibraltar is part of your long-term plan, property specialists can help couples align wedding decisions with future relocation goals.',
  };
  const aeoAngle = categoryAeoAngles[categoryKey] || `Compare specialist ${categoryLower} providers by clarity, responsiveness, and delivery confidence before confirming your booking.`;
  const introOverride = categoryIntroOverrides[categoryKey];

  const selectedFaqs = categoryFaqOverrides[categoryKey] || faqByType[contentType] || faqByType.general;
  const metaDescriptionBase = `Find trusted wedding ${categoryLower} in Gibraltar. ${aeoAngle}`;

  return {
    ...selectedTemplate,
    ...(introOverride || {}),
    faqs: selectedFaqs,
    aeoAngle,
    metaDescription: metaDescriptionBase.length > 158 ? `${metaDescriptionBase.slice(0, 155).trimEnd()}...` : metaDescriptionBase,
  };
};

export default function VendorsPage({ initialCategory: propCategory, seoSlug }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [categoriesWithIcons, setCategoriesWithIcons] = useState(defaultCategories);
  
  const isSeoLanding = Boolean(seoSlug);
  const seoPreviewMode = getSeoPreviewMode(location.search);
  const isWeddingVenuesSeoPage = String(seoSlug || '') === WEDDING_VENUES_SEO_SLUG;
  const showAllVenueCards = searchParams.get('showAll') === '1';

  // Get category from prop (SEO URL), URL slug, or direct name
  const categoryParam = searchParams.get('category') || '';
  const initialCategory = propCategory || slugToCategory[categoryParam] || categoryParam || 'All Categories';
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const isPlannerOrAllInOneCategory = useMemo(() => {
    const value = String(selectedCategory || '').toLowerCase();
    return value.includes('wedding planner') || value.includes('all in one registry package');
  }, [selectedCategory]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        const apiCategories = Array.isArray(response.data) ? response.data : [];
        const categoriesSource = seoPreviewMode
          ? apiCategories
          : apiCategories.filter(isCategoryEnabled);

        setCategoriesWithIcons(mergeOrderedCategoryObjects(categoriesSource, defaultCategories));
      } catch (error) {
        // Use defaults on error
      }
    };
    fetchCategories();
  }, [seoPreviewMode]);

  const CATEGORIES = ['All Categories', ...categoriesWithIcons.map(c => c.name)];

  const seoCategoryLabel = useMemo(() => formatWeddingCategoryLabel(selectedCategory), [selectedCategory]);
  const seoCategoryLower = seoCategoryLabel.toLowerCase();
  const seoContent = useMemo(() => buildSeoCategoryContent(seoCategoryLabel), [seoCategoryLabel]);

  const upsertMetaTag = (attributeName, attributeValue, content) => {
    if (typeof document === 'undefined') return;
    let tag = document.querySelector(`meta[${attributeName}='${attributeValue}']`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attributeName, attributeValue);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };

  const upsertCanonical = (href) => {
    if (typeof document === 'undefined') return;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  };

  const upsertJsonLd = (id, payload) => {
    if (typeof document === 'undefined') return;
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(payload);
  };

  const removeJsonLd = (id) => {
    if (typeof document === 'undefined') return;
    const script = document.getElementById(id);
    if (script) script.remove();
  };

  // Update page title, description, canonical, robots, and schema
  useEffect(() => {
    const isCategorySelected = selectedCategory && selectedCategory !== 'All Categories';
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://gibraltarweddingexpo.online';
    const seoPath = isCategorySelected ? categoryNameToSeoPath(selectedCategory) : '/vendors';
    const hasFilterCategoryQuery = location.pathname === '/vendors' && Boolean(searchParams.get('category'));
    const hasSearchQuery = location.pathname === '/vendors' && Boolean(searchParams.get('search'));
    const shouldNoindex = !isSeoLanding && (hasFilterCategoryQuery || hasSearchQuery);

    if (isSeoLanding && isCategorySelected) {
      document.title = `Wedding ${seoCategoryLabel} in Gibraltar | Gibraltar Wedding Expo`;
      upsertMetaTag(
        'name',
        'description',
        seoContent.metaDescription
      );
      upsertMetaTag('name', 'robots', 'index, follow');
      upsertCanonical(`${origin}${seoPath}`);

      upsertJsonLd('seo-category-faq-jsonld', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: (seoContent.faqs || []).map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a,
          },
        })),
      });

      upsertJsonLd('seo-category-breadcrumb-jsonld', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name: 'Vendors', item: `${origin}/vendors` },
          { '@type': 'ListItem', position: 3, name: `Wedding ${seoCategoryLabel} in Gibraltar`, item: `${origin}${seoPath}` },
        ],
      });
    } else if (isCategorySelected) {
      document.title = `Wedding ${seoCategoryLabel} in Gibraltar | Gibraltar Wedding Expo`;
      upsertMetaTag(
        'name',
        'description',
        seoContent.metaDescription
      );
      upsertMetaTag('name', 'robots', shouldNoindex ? 'noindex, follow' : 'index, follow');
      upsertCanonical(`${origin}${hasFilterCategoryQuery ? categoryNameToSeoPath(selectedCategory) : '/vendors'}`);
      removeJsonLd('seo-category-faq-jsonld');
      removeJsonLd('seo-category-breadcrumb-jsonld');
    } else {
      document.title = 'Wedding Vendors in Gibraltar | Gibraltar Wedding Expo';
      upsertMetaTag('name', 'description', 'Browse trusted wedding vendors in Gibraltar. Compare suppliers, view profiles, and start planning your perfect day.');
      upsertMetaTag('name', 'robots', shouldNoindex ? 'noindex, follow' : 'index, follow');
      upsertCanonical(`${origin}/vendors`);
      removeJsonLd('seo-category-faq-jsonld');
      removeJsonLd('seo-category-breadcrumb-jsonld');
    }
  }, [categoriesWithIcons, isSeoLanding, location.pathname, searchParams, selectedCategory, seoCategoryLabel, seoCategoryLower, seoContent.metaDescription]);

  // Sync category from props when it changes (for SEO URLs)
  useEffect(() => {
    if (propCategory && propCategory !== selectedCategory) {
      setSelectedCategory(propCategory);
    }
  }, [propCategory]);

  // Redirect legacy query-category URLs to canonical SEO category slugs
  useEffect(() => {
    if (isSeoLanding || location.pathname !== '/vendors') return;

    const queryCategory = searchParams.get('category');
    if (!queryCategory) return;

    const resolvedCategory = slugToCategory[queryCategory]
      || categoriesWithIcons.find((cat) => String(cat.name || '').toLowerCase() === String(queryCategory || '').toLowerCase())?.name;

    if (!resolvedCategory || resolvedCategory === 'All Categories') return;

    const nextPath = categoryNameToSeoPath(resolvedCategory);
    const nextSearch = searchParams.get('search');
    const targetUrl = nextSearch
      ? `${nextPath}?search=${encodeURIComponent(nextSearch)}`
      : nextPath;

    navigate(targetUrl, { replace: true });
  }, [categoriesWithIcons, isSeoLanding, location.pathname, navigate, searchParams]);

  useEffect(() => {
    fetchVendors();
  }, [selectedCategory, searchParams]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      if (isWeddingVenuesSeoPage) {
        const allVenueResponse = await axios.get(`${API}/vendors?page=1&limit=300`);
        const allVendors = Array.isArray(allVenueResponse.data) ? allVenueResponse.data : [];
        const venueVendors = allVendors.filter(isWeddingVenueVendor);
        const queryLower = String(searchQuery || '').trim().toLowerCase();
        const filteredBySearch = queryLower
          ? venueVendors.filter((vendor) => {
              const haystack = [
                vendor?.name,
                vendor?.description,
                vendor?.location,
                ...(Array.isArray(vendor?.categories) ? vendor.categories : []),
              ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return haystack.includes(queryLower);
            })
          : venueVendors;

        setVendors(filteredBySearch);
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'All Categories') {
        params.append('category', selectedCategory);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await axios.get(`${API}/vendors?${params.toString()}`);
      const filteredVendors = Array.isArray(response.data) ? response.data : [];

      if (
        selectedCategory &&
        selectedCategory !== 'All Categories' &&
        filteredVendors.length === 0 &&
        !isSeoLanding
      ) {
        const fallbackResponse = await axios.get(`${API}/vendors?page=1&limit=24`);
        const fallbackVendors = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : [];
        setVendors(fallbackVendors);
      } else {
        setVendors(filteredVendors);
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (selectedCategory && selectedCategory !== 'All Categories') {
      const slug = categoryToSlug[selectedCategory] || selectedCategory;
      params.set('category', slug);
    }
    if (searchQuery) {
      params.set('search', searchQuery);
    }
    setSearchParams(params);
    fetchVendors();
  };

  const handleCategoryChange = (value) => {
    if (value && value !== 'All Categories') {
      navigate(categoryNameToSeoPath(value));
      return;
    }

    if (isSeoLanding) {
      navigate('/vendors');
      return;
    }

    setSelectedCategory('All Categories');
    const params = new URLSearchParams(searchParams);
    params.delete('category');
    setSearchParams(params);
  };

  const clearFilters = () => {
    if (isSeoLanding) {
      navigate('/vendors');
      return;
    }
    setSearchQuery('');
    setSelectedCategory('All Categories');
    setSearchParams({});
  };

  const hasFilters = searchQuery || (selectedCategory && selectedCategory !== 'All Categories');
  const showSeoLandingLayout = isSeoLanding && selectedCategory !== 'All Categories';
  const shouldLimitWeddingVenueCards = showSeoLandingLayout && isWeddingVenuesSeoPage && !showAllVenueCards;
  const displayedVendors = shouldLimitWeddingVenueCards ? vendors.slice(0, 12) : vendors;
  const hasMoreWeddingVenueCards = shouldLimitWeddingVenueCards && vendors.length > 12;

  const churchesContactBlock = showSeoLandingLayout && isWeddingVenuesSeoPage ? (
    <section
      className="mt-8 rounded-xl border border-[#D4AF37]/35 bg-white p-5 md:p-7 shadow-[0_12px_30px_rgba(28,25,23,0.04)]"
      data-testid="wedding-venues-churches-contact-section"
    >
      <h2 className="text-2xl font-semibold text-[#5C0120]" data-testid="wedding-venues-churches-contact-h2">
        Gibraltar churches for wedding ceremony enquiries
      </h2>
      <p className="mt-3 text-stone-700 leading-relaxed" data-testid="wedding-venues-churches-contact-intro-1">
        Couples planning a church ceremony in Gibraltar often contact the church office directly to check ceremony eligibility,
        available dates, required documents, and pre-marriage guidance. The contacts below are commonly used starting points for
        church wedding enquiries in Gibraltar.
      </p>
      <p className="mt-3 text-stone-700 leading-relaxed" data-testid="wedding-venues-churches-contact-intro-2">
        Always confirm the latest requirements and availability directly with the church before making final bookings.
      </p>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wedding-venues-churches-contact-grid">
        {CHURCH_CONTACTS.map((church) => (
          <article
            key={church.id}
            className="rounded-sm border border-stone-200 bg-stone-50 p-4"
            data-testid={`wedding-venues-church-card-${church.id}`}
          >
            <h3 className="text-base font-semibold text-stone-900" data-testid={`wedding-venues-church-name-${church.id}`}>
              {church.name}
            </h3>
            <p className="mt-2 text-sm text-stone-700" data-testid={`wedding-venues-church-summary-${church.id}`}>
              {church.summary}
            </p>
            <div className="mt-3 space-y-1 text-sm text-stone-700">
              <p data-testid={`wedding-venues-church-phone-${church.id}`}>
                <span className="font-medium">Phone:</span>{' '}
                <a href={`tel:${church.phone.replace(/\s+/g, '')}`} className="text-[#5C0120] hover:underline" data-testid={`wedding-venues-church-phone-link-${church.id}`}>
                  {church.phone}
                </a>
              </p>
              {church.email && (
                <p data-testid={`wedding-venues-church-email-${church.id}`}>
                  <span className="font-medium">Email:</span>{' '}
                  <a href={`mailto:${church.email}`} className="text-[#5C0120] hover:underline" data-testid={`wedding-venues-church-email-link-${church.id}`}>
                    {church.email}
                  </a>
                </p>
              )}
              <p data-testid={`wedding-venues-church-website-${church.id}`}>
                <span className="font-medium">Website:</span>{' '}
                <a href={church.website} target="_blank" rel="noreferrer" className="text-[#5C0120] hover:underline" data-testid={`wedding-venues-church-website-link-${church.id}`}>
                  Visit church website
                </a>
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  ) : null;

  const seoContentBlock = showSeoLandingLayout ? (
    <div className="mt-12 max-w-4xl mx-auto text-left md:text-center space-y-4" data-testid="seo-category-landing-header-content">
      <p className="text-stone-600 leading-relaxed" data-testid="seo-category-landing-intro-paragraph-1">{seoContent.intro1}</p>
      <p className="text-stone-600 leading-relaxed" data-testid="seo-category-landing-intro-paragraph-2">{seoContent.intro2}</p>
      <p className="text-stone-600 leading-relaxed" data-testid="seo-category-landing-aeo-angle">{seoContent.aeoAngle}</p>
      <p className="text-stone-600 leading-relaxed" data-testid="seo-category-landing-intro-paragraph-3">
        On this page you can browse Gibraltar-based {seoCategoryLower}, compare supplier approaches, and contact vendors directly to discuss availability.
        {!isPlannerOrAllInOneCategory && (
          <>
            {' '}If you are planning your timeline, read our{' '}
            <Link to="/getting-married-in-gibraltar" className="text-[#5C0120] hover:underline" data-testid="seo-category-internal-link-getting-married">
              Getting Married in Gibraltar guide
            </Link>
            ,{' '}
            <Link to="/how-to-get-married-in-gibraltar" className="text-[#5C0120] hover:underline" data-testid="seo-category-internal-link-how-to">
              step-by-step process guide
            </Link>
            , and{' '}
            <Link to="/cost-of-getting-married-in-gibraltar" className="text-[#5C0120] hover:underline" data-testid="seo-category-internal-link-costs">
              wedding cost breakdown
            </Link>
            .
          </>
        )}
      </p>

      <div className="space-y-4 pt-1" data-testid="seo-category-landing-h2-sections">
        <section data-testid="seo-category-h2-why-choose">
          <h2 className="text-2xl font-semibold text-stone-900">Why choose Gibraltar {seoCategoryLower}</h2>
          <p className="text-stone-600 mt-2">{seoContent.why[0]}</p>
          <p className="text-stone-600 mt-2">{seoContent.why[1]}</p>
        </section>

        <section data-testid="seo-category-h2-what-to-look-for">
          <h2 className="text-2xl font-semibold text-stone-900">What to look for in {seoCategoryLower}</h2>
          <p className="text-stone-600 mt-2">{seoContent.lookFor[0]}</p>
          <p className="text-stone-600 mt-2">{seoContent.lookFor[1]}</p>
        </section>

        <section data-testid="seo-category-h2-browse-trusted">
          <h2 className="text-2xl font-semibold text-stone-900">Browse {seoCategoryLower} in Gibraltar</h2>
          <p className="text-stone-600 mt-2">{seoContent.browse}</p>
        </section>

        <section data-testid="seo-category-h2-faqs">
          <h2 className="text-2xl font-semibold text-stone-900">FAQs about {seoCategoryLower}</h2>
          <div className="mt-3 space-y-3" data-testid="seo-category-faq-list">
            {(seoContent.faqs || []).map((faq, index) => (
              <article key={`${faq.q}-${index}`} className="rounded-sm border border-stone-200 bg-stone-50 p-4" data-testid={`seo-category-faq-item-${index + 1}`}>
                <h3 className="text-base font-semibold text-stone-900" data-testid={`seo-category-faq-question-${index + 1}`}>{faq.q}</h3>
                <p className="mt-1 text-sm text-stone-700" data-testid={`seo-category-faq-answer-${index + 1}`}>{faq.a}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  ) : null;

  return (
    <div className="min-h-screen py-8" data-testid="vendors-page">
      {/* Header */}
      <div className="container-wedding mb-4">
        <div className="text-center mb-4">
          {seoPreviewMode && (
            <p className="inline-flex items-center rounded-full border border-[#D4AF37]/60 bg-[#fff7e6] px-3 py-1 text-xs font-medium text-[#5C0120]" data-testid="seo-preview-mode-badge">
              SEO preview mode is ON (showing full category set for review)
            </p>
          )}
          {showSeoLandingLayout ? (
            <div className="max-w-4xl mx-auto text-left md:text-center space-y-4" data-testid="seo-category-landing-title-only-content">
              <h1 className="heading-xl" data-testid="seo-category-landing-h1">Wedding {seoCategoryLabel} in Gibraltar</h1>
            </div>
          ) : (
            <>
              <p className="caption mb-2">Find Your Perfect Match</p>
              <h1 className="heading-xl mb-2">Wedding Vendors</h1>
              <p className="text-stone-600 max-w-2xl mx-auto">
                Discover Gibraltar's finest wedding professionals.
              </p>
            </>
          )}
        </div>

        {/* Category Thumbnails */}
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 xl:grid-cols-15 gap-2 mb-4">
          {categoriesWithIcons.map((category) => (
            <button
              key={category.name}
              onClick={() => handleCategoryChange(category.name)}
              className={`group flex flex-col items-center p-2 rounded-sm border transition-all duration-200 ${
                selectedCategory === category.name
                  ? 'bg-[#5C0120] border-[#5C0120] text-white'
                  : 'bg-white border-stone-100 hover:border-[#D4AF37] card-shadow'
              }`}
              data-testid={`category-btn-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {category.icon === 'gib-flag' ? (
                <img 
                  src="https://flagcdn.com/w40/gi.png" 
                  alt="Gibraltar Flag" 
                  className="h-5 w-auto mb-1"
                />
              ) : (
                <span className="text-xl mb-1">{category.icon}</span>
              )}
              <span className={`text-xs text-center font-medium leading-tight ${
                selectedCategory === category.name ? 'text-white' : 'text-stone-700 group-hover:text-[#5C0120]'
              }`}>
                {category.name}
              </span>
            </button>
          ))}
        </div>

        {/* Search & Filters */}
        <div className="bg-white p-4 rounded-sm border border-stone-100 card-shadow">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
              <Input
                type="text"
                placeholder="Search vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-wedding"
                data-testid="vendor-search-input"
              />
            </div>

            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full md:w-[200px] input-wedding" data-testid="category-select">
                <Filter className="h-4 w-4 mr-2 text-stone-400" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button type="submit" className="btn-primary" data-testid="search-submit">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </form>

          {/* Active Filters */}
          {hasFilters && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-stone-100">
              <span className="text-sm text-stone-500">Active filters:</span>
              {selectedCategory && selectedCategory !== 'All Categories' && (
                <span className="inline-flex items-center gap-1 bg-[#5C0120]/10 text-[#5C0120] px-3 py-1 rounded-sm text-sm">
                  {selectedCategory}
                  <button onClick={() => handleCategoryChange('All Categories')}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center gap-1 bg-[#5C0120]/10 text-[#5C0120] px-3 py-1 rounded-sm text-sm">
                  "{searchQuery}"
                  <button onClick={() => {
                    setSearchQuery('');
                    const params = new URLSearchParams(searchParams);
                    params.delete('search');
                    setSearchParams(params);
                  }}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              <button onClick={clearFilters} className="text-sm text-[#5C0120] hover:underline" data-testid="clear-active-filters-button">
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="container-wedding">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-[#5C0120]" />
          </div>
        ) : displayedVendors.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-stone-500 text-lg mb-4">No vendors found matching your criteria.</p>
            <Button onClick={clearFilters} className="btn-primary" data-testid="empty-results-clear-filters-button">
              Clear Filters
            </Button>
            {churchesContactBlock}
            {seoContentBlock}
          </div>
        ) : (
          <>
            <p className="text-stone-500 mb-6">
              Showing {displayedVendors.length} vendor{displayedVendors.length !== 1 ? 's' : ''}
              {isWeddingVenuesSeoPage && !showAllVenueCards && vendors.length > displayedVendors.length ? ` of ${vendors.length}` : ''}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {displayedVendors.map((vendor, index) => (
                <div
                  key={vendor.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <VendorCard vendor={vendor} />
                </div>
              ))}
            </div>

            {hasMoreWeddingVenueCards && (
              <div className="mt-6 text-center" data-testid="wedding-venues-view-all-wrap">
                <Link
                  to={`${location.pathname}?showAll=1`}
                  className="inline-flex items-center rounded-sm border border-[#5C0120] px-4 py-2 text-sm font-medium text-[#5C0120] hover:bg-[#5C0120] hover:text-white transition-colors"
                  data-testid="wedding-venues-view-all-button"
                >
                  View all wedding venue vendors
                </Link>
              </div>
            )}

            {churchesContactBlock}
            {seoContentBlock}
          </>
        )}
      </div>
    </div>
  );
}
